<template>
  <div class="study-screen">
    <!-- Header -->
    <div class="screen-header">
      <button class="screen-header__back" @click="goBack">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 class="screen-header__title">{{ t("study_mode") }}</h1>
      <div class="screen-header__progress">
        {{ currentIndex + 1 }} / {{ cards.length }}
      </div>
    </div>

    <!-- Content -->
    <div class="study-content">
      <!-- Loading -->
      <div v-if="loading" class="study-loading">
        <div class="spinner"></div>
        <span>{{ t("loading") }}</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="study-error">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ef4444"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p>{{ error }}</p>
        <button class="btn btn--primary" @click="fetchCards">
          {{ t("try_again") }}
        </button>
      </div>

      <!-- Empty -->
      <div v-else-if="cards.length === 0" class="study-empty">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9ca3af"
          stroke-width="1"
        >
          <polygon points="12,2 2,7 12,12 22,7 12,2" />
          <polyline points="2,17 12,22 22,17" />
          <polyline points="2,12 12,17 22,12" />
        </svg>
        <p>{{ t("no_flash_cards_yet") }}</p>
      </div>

      <!-- Study Complete -->
      <div v-else-if="isComplete" class="study-complete">
        <div class="complete-icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <polyline points="22,4 12,14.01 9,11.01" />
          </svg>
        </div>
        <h2>{{ t("study_complete") }}</h2>
        <div class="complete-stats">
          <div class="stat">
            <span class="stat__value stat__value--success">{{
              correctCount
            }}</span>
            <span class="stat__label">{{ t("correct") }}</span>
          </div>
          <div class="stat">
            <span class="stat__value stat__value--error">{{
              incorrectCount
            }}</span>
            <span class="stat__label">{{ t("incorrect") }}</span>
          </div>
        </div>
        <div class="complete-actions">
          <button class="btn btn--primary" @click="restartStudy">
            {{ t("study_again") }}
          </button>
          <button class="btn btn--secondary" @click="goBack">
            {{ t("back_to_deck") }}
          </button>
        </div>
      </div>

      <!-- Flash Card -->
      <div v-else class="card-container">
        <div
          class="flash-card"
          :class="{ 'flash-card--flipped': isFlipped }"
          @click="flipCard"
        >
          <div class="flash-card__front">
            <span class="flash-card__label">{{ t("question") }}</span>
            <p class="flash-card__content">
              {{ currentCard?.front || currentCard?.question }}
            </p>
            <span class="flash-card__hint">{{ t("tap_to_reveal") }}</span>
          </div>
          <div class="flash-card__back">
            <span class="flash-card__label">{{ t("answer") }}</span>
            <p class="flash-card__content">
              {{ currentCard?.back || currentCard?.answer }}
            </p>
          </div>
        </div>

        <!-- Answer Buttons -->
        <div v-if="isFlipped" class="answer-buttons">
          <button
            class="answer-btn answer-btn--incorrect"
            @click="handleAnswer(false)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>{{ t("incorrect") }}</span>
          </button>
          <button
            class="answer-btn answer-btn--correct"
            @click="handleAnswer(true)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="20,6 9,17 4,12" />
            </svg>
            <span>{{ t("correct") }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div
      v-if="!loading && !error && cards.length > 0 && !isComplete"
      class="progress-bar-container"
    >
      <div class="progress-bar">
        <div
          class="progress-bar__fill"
          :style="{ width: `${(currentIndex / cards.length) * 100}%` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { useI18n } from "../i18n";

export default {
  name: "StudyModeScreen",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const miaStore = useMiaStore();
    const { t, isRtl } = useI18n();

    const deckId = ref(null);
    const cards = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const currentIndex = ref(0);
    const isFlipped = ref(false);
    const correctCount = ref(0);
    const incorrectCount = ref(0);

    const currentCard = computed(() => cards.value[currentIndex.value] || null);
    const isComplete = computed(
      () => currentIndex.value >= cards.value.length && cards.value.length > 0
    );

    const fetchCards = async () => {
      loading.value = true;
      error.value = null;

      const result = await miaStore.getStudyCards(deckId.value);

      if (result.success) {
        cards.value = result.data?.cards || result.data || [];
      } else {
        error.value = result.error || "Failed to load cards";
      }

      loading.value = false;
    };

    const flipCard = () => {
      if (!isFlipped.value) {
        isFlipped.value = true;
      }
    };

    const handleAnswer = async (isCorrect) => {
      if (isCorrect) {
        correctCount.value++;
      } else {
        incorrectCount.value++;
      }

      // Update progress on backend
      if (currentCard.value?.id) {
        await miaStore.updateCardProgress(currentCard.value.id, isCorrect);
      }

      // Move to next card
      isFlipped.value = false;
      currentIndex.value++;
    };

    const restartStudy = () => {
      currentIndex.value = 0;
      correctCount.value = 0;
      incorrectCount.value = 0;
      isFlipped.value = false;
      // Shuffle cards
      cards.value = [...cards.value].sort(() => Math.random() - 0.5);
    };

    const goBack = () => {
      router.push("/mia/flash-cards");
    };

    onMounted(() => {
      deckId.value = route.query.deckId;

      if (!deckId.value) {
        router.push("/mia/flash-cards");
        return;
      }

      fetchCards();
    });

    return {
      t,
      isRtl,
      cards,
      loading,
      error,
      currentIndex,
      isFlipped,
      correctCount,
      incorrectCount,
      currentCard,
      isComplete,
      fetchCards,
      flipCard,
      handleAnswer,
      restartStudy,
      goBack,
    };
  },
};
</script>

<style scoped>
.study-screen {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.screen-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.screen-header__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: white;
  transition: background 0.2s ease;
}

.screen-header__back:hover {
  background: rgba(255, 255, 255, 0.3);
}

.screen-header__title {
  flex: 1;
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.screen-header__progress {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
}

.study-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.study-loading,
.study-error,
.study-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.study-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: white;
}

.complete-icon {
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.study-complete h2 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 24px;
}

.complete-stats {
  display: flex;
  gap: 32px;
  margin-bottom: 32px;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat__value {
  font-size: 36px;
  font-weight: 700;
}

.stat__value--success {
  color: #22c55e;
}
.stat__value--error {
  color: #ef4444;
}

.stat__label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.complete-actions {
  display: flex;
  gap: 16px;
}

.card-container {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.flash-card {
  width: 100%;
  height: 300px;
  perspective: 1000px;
  cursor: pointer;
  margin-bottom: 32px;
}

.flash-card__front,
.flash-card__back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: transform 0.6s;
}

.flash-card__front {
  transform: rotateY(0deg);
}

.flash-card__back {
  transform: rotateY(180deg);
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
}

.flash-card--flipped .flash-card__front {
  transform: rotateY(-180deg);
}

.flash-card--flipped .flash-card__back {
  transform: rotateY(0deg);
}

.flash-card__label {
  font-size: 12px;
  font-weight: 600;
  color: #6366f1;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
}

.flash-card__content {
  font-size: 18px;
  font-weight: 500;
  color: #1f2937;
  text-align: center;
  line-height: 1.6;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
}

.flash-card__hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 16px;
}

.answer-buttons {
  display: flex;
  gap: 16px;
  width: 100%;
}

.answer-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border: none;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.answer-btn--incorrect {
  background: rgba(239, 68, 68, 0.2);
  color: white;
}

.answer-btn--incorrect:hover {
  background: rgba(239, 68, 68, 0.4);
}

.answer-btn--correct {
  background: rgba(34, 197, 94, 0.2);
  color: white;
}

.answer-btn--correct:hover {
  background: rgba(34, 197, 94, 0.4);
}

.progress-bar-container {
  padding: 16px 24px 24px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar__fill {
  height: 100%;
  background: white;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.btn {
  padding: 14px 28px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn--primary {
  background: white;
  color: #6366f1;
  border: none;
}

.btn--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn--secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
}

.btn--secondary:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
